import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  Card,
  CardBody,
  SimpleGrid,
  Spinner,
  VStack,
  Button,
  Stat,
  StatLabel,
  StatNumber,
  Icon,
  useColorModeValue,
  Progress,
} from '@chakra-ui/react';
import { Brain, RefreshCw } from 'lucide-react';
import { useROICalculation } from '../hooks/useROICalculation';
import { useIframeResize } from '../hooks/useIframeResize';
import { formatCurrency, formatPercentage, formatNumber } from '../utils/formatters';

// Loading stages for dynamic animation
const loadingStages = [
  { text: "Analyzing your business data...", duration: 3000 },
  { text: "Researching market conditions...", duration: 4000 },
  { text: "Calculating industry benchmarks...", duration: 3500 },
  { text: "Analyzing competitor landscape...", duration: 3000 },
  { text: "Optimizing performance metrics...", duration: 4000 },
  { text: "Generating ROI projections...", duration: 2000 },
  { text: "Finalizing your analysis...", duration: 2000 }
];

const DynamicLoadingComponent = ({ textColor }) => {
  const [currentStage, setCurrentStage] = useState(0);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let stageTimer;
    let progressTimer;

    const startStage = (stageIndex) => {
      if (stageIndex >= loadingStages.length) {
        // Cycle back to first stage if analysis is still running
        setCurrentStage(0);
        setProgress(0);
        startStage(0);
        return;
      }

      setCurrentStage(stageIndex);
      setProgress(0);

      const stage = loadingStages[stageIndex];
      const progressIncrement = 100 / (stage.duration / 50); // Update every 50ms

      progressTimer = setInterval(() => {
        setProgress(prev => {
          const newProgress = prev + progressIncrement;
          return newProgress >= 100 ? 100 : newProgress;
        });
      }, 50);

      stageTimer = setTimeout(() => {
        clearInterval(progressTimer);
        startStage(stageIndex + 1);
      }, stage.duration);
    };

    startStage(0);

    return () => {
      clearTimeout(stageTimer);
      clearInterval(progressTimer);
    };
  }, []);

  return (
    <VStack spacing={6} maxW="400px">
      <Spinner size="lg" color="blue.500" thickness="3px" />
      <VStack spacing={3} textAlign="center">
        <Text
          color={textColor}
          fontSize="md"
          fontWeight="medium"
          minH="24px"
          transition="all 0.3s ease"
        >
          {loadingStages[currentStage]?.text}
        </Text>
        <Box w="full" maxW="300px">
          <Progress
            value={progress}
            size="sm"
            colorScheme="blue"
            borderRadius="full"
            bg="gray.100"
            transition="all 0.1s ease"
          />
        </Box>
        <Text fontSize="xs" color="gray.500">
          AI is processing your data...
        </Text>
      </VStack>
    </VStack>
  );
};

const ResultsPage = () => {
  const { results, loading, error, retry } = useROICalculation();

  // Color mode values
  const cardBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const headingColor = useColorModeValue('gray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const reasoningBg = useColorModeValue('blue.50', 'blue.900');

  // Auto-adjust iframe height when content changes
  const updateParentHeight = useIframeResize([loading, error, results]);

  // Trigger height update when results are fully loaded and rendered
  useEffect(() => {
    if (results && !loading && !error) {
      // Wait for DOM to be fully rendered, then update height
      setTimeout(() => {
        console.log('📊 Results page fully loaded, updating iframe height');
        updateParentHeight();
      }, 100);
    }
  }, [results, loading, error, updateParentHeight]);

  if (loading) {
    return (
      <Box minH="400px" display="flex" alignItems="center" justifyContent="center" px={4}>
        <DynamicLoadingComponent textColor={textColor} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box minH="400px" display="flex" alignItems="center" justifyContent="center">
        <Card maxW="md" bg={cardBg} borderColor={borderColor}>
          <CardBody textAlign="center" p={8}>
            <VStack spacing={4}>
              <Icon as={Brain} boxSize={8} color="red.500" />
              <Text color="red.500" fontSize="lg" fontWeight="bold">
                Error
              </Text>
              <Text color={textColor}>{error}</Text>
              <Button
                leftIcon={<RefreshCw size={16} />}
                colorScheme="blue"
                variant="outline"
                onClick={retry}
                size="sm"
              >
                Try Again
              </Button>
            </VStack>
          </CardBody>
        </Card>
      </Box>
    );
  }

  if (!results) {
    return (
      <Box minH="400px" display="flex" alignItems="center" justifyContent="center">
        <VStack spacing={4}>
          <Text color={textColor}>No results available</Text>
          <Button
            leftIcon={<RefreshCw size={16} />}
            colorScheme="blue"
            variant="outline"
            onClick={retry}
            size="sm"
          >
            Retry
          </Button>
        </VStack>
      </Box>
    );
  }

  const { monthlyMetrics, performanceMetrics } = results;

  return (
    <Container maxW="4xl" py={8}>
      <VStack spacing={8} align="stretch">

        {/* Main ROI Results Card */}
        <Card
          overflow="hidden"
          position="relative"
          shadow="none"
        >

          <CardBody p={0} pt={8}>
            <VStack spacing={8} align="center">
              {/* Header */}
              <VStack spacing={3} textAlign="center">
                <Heading size="lg" color={headingColor}>
                  Your Google Ads Earnings Potential
                </Heading>
                <Text fontSize="md" color={textColor} maxW="md">
                  Based on your business information and market analysis
                </Text>
              </VStack>

              {/* Primary Revenue Metric */}
              <VStack spacing={2} textAlign="center">
                <Text fontSize="sm" color={textColor} fontWeight="medium">
                  Estimated Monthly Revenue
                </Text>
                <Heading
                  size="xl"
                  color="green.600"
                  fontWeight="extrabold"
                  letterSpacing="tight"
                  marginBottom={2}
                >
                  {formatCurrency(monthlyMetrics?.projectedMonthlyRevenue)}
                </Heading>
              </VStack>

              {/* Key Metrics Grid */}
              <SimpleGrid columns={{ base: 2, md: 2 }} spacing={4} w="full" maxW="300px">
                <Stat textAlign="center">
                  <StatLabel color={textColor} fontSize="sm" mb={2}>
                    Monthly Visitors
                  </StatLabel>
                  <StatNumber fontSize="sm" color={headingColor}>
                    {formatNumber(monthlyMetrics?.monthlyClicks)}
                  </StatNumber>
                </Stat>

                <Stat textAlign="center">
                  <StatLabel color={textColor} fontSize="sm" mb={2}>
                    New Customers
                  </StatLabel>
                  <StatNumber fontSize="sm" color={headingColor}>
                    {formatNumber(monthlyMetrics?.monthlyConversions)}
                  </StatNumber>
                </Stat>
              </SimpleGrid>

              {/* AI Reasoning Section */}
              {results.reasoning && (
                <Box
                  bg={reasoningBg}
                  borderRadius="lg"
                  p={4}
                  maxW="2xl"
                  borderLeft="4px solid"
                  borderLeftColor="blue.500"
                >
                  <VStack spacing={3} align="start">
                    <Text fontSize="sm" fontWeight="semibold" color="blue.600">
                      Why This Estimate Is Achievable
                    </Text>
                    <Text fontSize="sm" color={textColor} lineHeight="tall">
                      {results.reasoning}
                    </Text>
                  </VStack>
                </Box>
              )}

              {/* Explanation Text */}
              <Text fontSize="sm" color={textColor} textAlign="center" lineHeight="tall" maxW="2xl">
                These estimates are based on industry data, your inputs, and different targeting styles — from high-quality leads to high-volume reach.

              </Text>
            </VStack>
          </CardBody>
        </Card>
      </VStack>
    </Container>
  );
};

export default ResultsPage; 